import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import agent from "../../../agent";
import PlanDialog from "./PlanDialog";
import PlanImportDialog from "./PlanImportDialog";
import IntegrityReport from "./IntegrityReport";
import { isTariffSet, planKindLabel } from "./mileTypes";

/**
 * Именованные тарифные карты — pricing.tariff_plans в новом калькуляторе.
 *
 * Карта — это цена партнёра на направление: ни перевозчика, ни услуги у неё нет,
 * кто повезёт и за сколько дней — решает маршрутизация. Цена из карты финальная:
 * наценка платформы поверх неё не начисляется.
 *
 * Приоритет у карты: покрыла она направление — цена берётся из неё, не покрыла —
 * обычный расчёт по тарифу перевозчика со скидкой плеча, если она задана.
 *
 * Назначение карты партнёру живёт в разделе «Скидки партнёров»: там на каждое
 * плечо выбирается карта и задаётся процент.
 *
 * Комплект (расчёт v2) — карта без плеча со всеми разделами. Включить его можно
 * только целым: калькулятор проверяет, что комплект покрывает цепочку «забор →
 * магистраль → доставка» целиком, и отчёт об этом открывается кнопкой в строке.
 */
const TariffPlans = () => {
  const toast = React.useRef(null);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [dialogVisible, setDialogVisible] = React.useState(false);
  const [editPlan, setEditPlan] = React.useState(null);

  const [importPlan, setImportPlan] = React.useState(null);
  const [deletion, setDeletion] = React.useState(null);
  const [integrity, setIntegrity] = React.useState(null);

  const showError = (detail) => toast.current?.show({ severity: "error", summary: "Ошибка", detail, life: 6000 });
  const showSuccess = (detail) => toast.current?.show({ severity: "success", summary: "Успешно", detail, life: 3000 });

  const load = React.useCallback(() => {
    setLoading(true);
    agent.TariffPlans.list()
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch((err) => showError(readError(err)))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const onSave = (data) => {
    setSaving(true);
    const request = editPlan
      ? agent.TariffPlans.update(editPlan.id, data)
      : agent.TariffPlans.create(data);

    request
      .then(() => {
        showSuccess(editPlan ? "Карта сохранена" : "Карта создана — теперь залейте в неё файл");
        setDialogVisible(false);
        setEditPlan(null);
        load();
      })
      .catch((err) => showError(readError(err)))
      .finally(() => setSaving(false));
  };

  /**
   * Тумблер меняет только признак «включена», остальные поля уходят как есть:
   * PUT замещает карту целиком, и отправить одну колонку нельзя.
   */
  const onToggleActive = (row, isActive) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive } : r)));
    agent.TariffPlans.update(row.id, {
      name: row.name,
      isActive,
      validFrom: row.validFrom,
      validTo: row.validTo,
    })
      .then(() => showSuccess(`Карта ${isActive ? "включена" : "выключена"}`))
      .catch((err) => {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive: !isActive } : r)));
        showError(readError(err));
      });
  };

  const openIntegrity = (row) => {
    agent.TariffPlans.integrity(row.id)
      .then((report) => setIntegrity({ plan: row, report }))
      .catch((err) => showError(readError(err)));
  };

  const openDeletion = (row) => {
    agent.TariffPlans.deletionPreview(row.id)
      .then((preview) => setDeletion({ plan: row, preview }))
      .catch((err) => showError(readError(err)));
  };

  const confirmDeletion = () => {
    const id = deletion.plan.id;
    agent.TariffPlans.remove(id)
      .then(() => {
        showSuccess("Карта удалена вместе с её содержимым");
        setDeletion(null);
        load();
      })
      .catch((err) => showError(readError(err)));
  };

  const periodBody = (row) => {
    if (!row.validFrom && !row.validTo) return <span title="Без ограничения по датам">бессрочно</span>;
    return `${row.validFrom ?? "…"} — ${row.validTo ?? "…"}`;
  };

  const contentBody = (row) =>
    row.routes > 0
      ? `${row.routes} направлений, ${row.ladderPoints} точек`
      : <span style={{ color: "#b45309" }} title="В карту не залит файл — партнёр будет считаться стандартным тарифом">пусто</span>;

  const actionsBody = (row) => (
    <div style={{ display: "flex", gap: 4 }}>
      <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" tooltip="Правка"
              onClick={() => { setEditPlan(row); setDialogVisible(true); }} />
      <Button icon="pi pi-upload" className="p-button-rounded p-button-text" tooltip="Залить файл"
              onClick={() => setImportPlan(row)} />
      {isTariffSet(row) && (
        <Button icon="pi pi-check-circle" className="p-button-rounded p-button-text" tooltip="Целостность комплекта"
                onClick={() => openIntegrity(row)} />
      )}
      <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger" tooltip="Удалить"
              onClick={() => openDeletion(row)} />
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />

      <div className="p-d-flex p-jc-between p-ai-center">
        <h1>Тарифные карты</h1>
        <Button label="Создать карту" icon="pi pi-plus"
                onClick={() => { setEditPlan(null); setDialogVisible(true); }} />
      </div>

      <Message severity="info" style={{ display: "block", marginBottom: 10 }}
               text="Карта — цена партнёра на направление, без привязки к перевозчику и услуге. Направление, которого в карте нет, считается обычным тарифом со скидкой плеча, если она задана. Назначается карта в разделе «Скидки партнёров». Комплект (расчёт v2) — одна книга со всеми разделами; включается только целым." />

      <DataTable value={rows} loading={loading} dataKey="id" emptyMessage="Карт пока нет">
        <Column field="name" header="Название" />
        <Column header="Плечо" body={(row) => planKindLabel(row)} style={{ width: "150px" }} />
        <Column header="Содержимое" body={contentBody} style={{ width: "200px" }} />
        <Column header="Срок" body={periodBody} style={{ width: "200px" }} />
        <Column header="Включена" style={{ width: "110px" }}
                body={(row) => <InputSwitch checked={!!row.isActive} onChange={(e) => onToggleActive(row, e.value)} />} />
        <Column header="" body={actionsBody} style={{ width: "180px" }} />
      </DataTable>

      <PlanDialog visible={dialogVisible} loading={saving} plan={editPlan}
                  onHide={() => { setDialogVisible(false); setEditPlan(null); }}
                  onSubmit={onSave} />

      <PlanImportDialog visible={Boolean(importPlan)} plan={importPlan}
                        onHide={() => setImportPlan(null)} onDone={load} />

      <Dialog header="Удалить карту?" visible={Boolean(deletion)} style={{ width: "520px" }} modal
              onHide={() => setDeletion(null)}
              footer={
                <div>
                  <Button label="Отмена" icon="pi pi-times" className="p-button-text"
                          onClick={() => setDeletion(null)} />
                  <Button label="Удалить" icon="pi pi-trash" severity="danger"
                          disabled={!deletion?.preview?.deletable} onClick={confirmDeletion} />
                </div>
              }>
        {deletion && (
          <div>
            <p>
              Карта «{deletion.plan.name}» ({planKindLabel(deletion.plan)}): будет удалено{" "}
              {deletion.preview.routes} направлений и {deletion.preview.ladderPoints} точек лестницы.
            </p>
            {deletion.preview.partnerBindings > 0 ? (
              <Message severity="error"
                       text={`Карта назначена партнёрам или клиентам (${deletion.preview.partnerBindings}). Сначала снимите её в разделе «Скидки партнёров» или в карточке клиента.`} />
            ) : (
              <Message severity="warn" text="Содержимое карты удаляется вместе с ней. Отменить нельзя — файл придётся заливать заново." />
            )}
          </div>
        )}
      </Dialog>

      <Dialog header={integrity ? `Целостность комплекта «${integrity.plan.name}»` : ""}
              visible={Boolean(integrity)} style={{ width: "760px" }} modal onHide={() => setIntegrity(null)}>
        {integrity && <IntegrityReport report={integrity.report} />}
      </Dialog>
    </div>
  );
};

const readError = (err) =>
  (typeof err === "string" ? err : null)
  || err?.response?.body?.messages?.[0]
  || err?.response?.body?.message
  || agent.handleError(err);

export default TariffPlans;
